import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { CheckIcon, useOmniFilter } from '@plerionhq/design-system';
import React, { useEffect } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  TenantModel,
  useDefaultTenant,
  useOrgTenants,
} from '../../api/tenants';
import { ProtectedComponent } from '../shared/ProtectedComponent/ProtectedComponent';
import { TenantRoute } from '../shared/TenantRoute/TenantRoute';

import { useTranslation } from 'react-i18next';

const redirectExceptionLinks = [
  {
    link: '/settings/integration/:integrationId',
    redirectLink: '/settings/integrations',
  },
  {
    link: '/settings/integrations/service-accounts/:serviceAccountId',
    redirectLink: '/settings/integrations/service-accounts/',
  },
  {
    link: '/settings/outbound-integration/:integrationId',
    redirectLink: '/settings/integrations',
  },
  {
    link: '/settings/profiles/:detectionSettingId/edit',
    redirectLink: '/settings/profiles',
  },
  {
    link: '/settings/workflows/:workflowId',
    redirectLink: '/settings/workflows',
  },
  {
    link: '/settings/custom-reports/:customReportId/editor',
    redirectLink: '/settings/custom-reports',
  },
  {
    link: '/settings/custom-reports/:customReportId/editor?mode=readOnly',
    redirectLink: '/settings/custom-reports',
  },
  {
    link: '/settings/well-architected/:wellArchitectedId',
    redirectLink: '/settings/well-architected',
  },
  {
    link: '/settings/compliance-frameworks/:customFrameworksId',
    redirectLink: '/settings/compliance-frameworks',
  },
  {
    link: '/compliance/:complianceId',
    redirectLink: '/compliance',
  },
  {
    link: '/well-architected/lenses/:frameworkId',
    redirectLink: '/well-architected/lenses',
  },
  {
    link: '/code-security/scans/:scanId',
    redirectLink: '/code-security',
  },
  {
    link: '/code-security/scans/:scanId/:tab',
    redirectLink: '/code-security',
  },
  {
    link: '/?integrationId=:integrationId',
    redirectLink: '/',
  },
  {
    link: '/alerts',
    redirectLink: `/alerts?statuses=OPEN&acknowledged=NO&sortBy=discovered&sortOrder=ASC`,
  },
  {
    link: '/findings',
    redirectLink: '/findings?status=FAILED',
  },
];

const getRedirectLink = (currentPath: string) => {
  let redirectLink = currentPath;
  const exceptionLink = redirectExceptionLinks.find((exception) =>
    matchPath(exception.link, currentPath),
  );
  if (exceptionLink) {
    redirectLink = exceptionLink.redirectLink;
  }
  return encodeURIComponent(redirectLink);
};

function TenantSwitch() {
  const { t } = useTranslation('containerInterface');
  const { isLoading, data: tenantData } = useOrgTenants();
  const { data: defaultTenant } = useDefaultTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState<TenantModel | null>(null);
  const { clearOmniFilter, clearRecentOmniFilters } = useOmniFilter();

  const activeTenants = tenantData?.filter(
    (tenant) =>
      !(
        tenant.status === 'delete_in_progress' ||
        tenant.status === 'delete_failed'
      ),
  );

  useEffect(() => {
    const currentTenant = tenantData?.find((x) => x.tenantId === defaultTenant);
    if (currentTenant) {
      setSelected(currentTenant);
    }
  }, [defaultTenant, tenantData]);

  if (!isLoading && activeTenants?.length === 0) {
    return (
      <ProtectedComponent permission={'organization:Administration'}>
        <Link
          to="/settings/tenants/add"
          className="bg-br-primary/10 text-br-primary focus:ring-br-primary hover:bg-br-primary/30 z-0 mx-6 my-1 flex items-center gap-2 rounded-md px-2 py-1 focus:ring-2 focus:ring-offset-2 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4" /> {t('addTenant')}
        </Link>
      </ProtectedComponent>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <div className="bg-lightest-gray mr-6 ml-4 h-8 w-8 animate-pulse rounded-md sm:w-60" />
      </div>
    );
  }

  return (
    <Listbox
      as="div"
      value={selected || undefined}
      onChange={async (value: TenantModel) => {
        if (value && value.tenantId !== defaultTenant) {
          clearOmniFilter();
          clearRecentOmniFilters();
          navigate(
            `/auth/switch-tenant/${value.tenantId}?tenantName=${
              value.name
            }&redirectTo=${getRedirectLink(location.pathname)}`,
          );
        }
      }}
      className="relative flex items-center"
    >
      {({ open }) => (
        <>
          <ListboxButton
            className={`relative mx-0 max-w-80 cursor-pointer truncate rounded-lg border bg-white py-1.5 pr-7 pl-11 overflow-ellipsis text-black outline-none dark:bg-gray-800 dark:text-gray-100 ${open ? 'border-action-primary' : 'border-light-gray dark:border-gray-600'}`}
            data-testid="tenant-switcher-input"
          >
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <OfficeBuildingIcon aria-hidden="true" />
            </span>
            {selected?.name || ''}
            {activeTenants && activeTenants.length > 0 && (
              <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-4 w-4 text-black dark:text-gray-300"
                />
              </span>
            )}
          </ListboxButton>

          {activeTenants && activeTenants.length > 0 && (
            <ListboxOptions className="absolute top-10 z-10 my-1 max-h-96 w-full min-w-80 overflow-auto rounded-md bg-white py-2 text-sm shadow-lg outline-none dark:bg-gray-800">
              {activeTenants.map((tenant) => (
                <ListboxOption
                  key={tenant.tenantId}
                  value={tenant}
                  className={({ active, selected }) =>
                    `hover:bg-select-hover relative mx-2 my-1 flex cursor-pointer flex-row items-center justify-between rounded p-1 text-black select-none dark:text-gray-100 ${selected ? 'bg-select-selected-background text-select-selected-text' : active ? 'bg-select-selected-background' : ''}`
                  }
                >
                  <span data-testid="tenant-switcher-option">
                    {tenant.name}
                  </span>
                  {selected?.tenantId === tenant.tenantId && (
                    <CheckIcon className="h-3 w-3" fill="#FF732E" />
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          )}
        </>
      )}
    </Listbox>
  );
}

export const TenantSwitcher = () => {
  const { isLoading, data: tenantData } = useOrgTenants();
  const activeTenants = tenantData?.filter(
    (tenant) =>
      !(
        tenant.status === 'delete_in_progress' ||
        tenant.status === 'delete_failed'
      ),
  );

  if (isLoading || (activeTenants && activeTenants.length < 2)) {
    return <></>;
  }

  return (
    <TenantRoute>
      <TenantSwitch />
    </TenantRoute>
  );
};

const OfficeBuildingIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 text-black dark:text-gray-300"
  >
    <path
      d="M14 8V0H4V4H0V18H8V14H10V18H18V8H14ZM4 16H2V14H4V16ZM4 12H2V10H4V12ZM4 8H2V6H4V8ZM8 12H6V10H8V12ZM8 8H6V6H8V8ZM8 4H6V2H8V4ZM12 12H10V10H12V12ZM12 8H10V6H12V8ZM12 4H10V2H12V4ZM16 16H14V14H16V16ZM16 12H14V10H16V12Z"
      fill="currentColor"
    />
  </svg>
);
